import React from 'react';

import { usePersistState } from '../hooks/usePersistState';
import Filter, {
  ConsumerFilter,
  DrivetrainFilter,
  EngineFilter,
  FilterAttributeKey,
  FilterNamePrefix,
  FuelFilter,
  MakeFilter,
  ModelFilter,
  OdometerFilter,
  OrganizationsFilter,
  PossibleFilters,
  PriceFilter,
  SavedListingFilter,
  SharedWithUserFilter,
  TransmissionFilter,
  YearFilter,
} from '../models/Filter';
import MakeModelFilterBuilder from '../services/MakeModelFilterBuilder';
import {
  FilterCountModelsQuery,
  FilterCountOrganizationsQuery,
  FilterCountStatusesQuery,
  Tag,
  useFilterCountModelsLazyQuery,
  useFilterCountOrganizationsLazyQuery,
  useFilterCountsLazyQuery,
  useFilterCountStatusesLazyQuery,
  VehicleFilters,
  VehicleStatus,
} from '../types/api.graphql';

interface OrganizationFilterData {
  key: string;
  value: string;
}

type FiltersContext = {
  handleRemoveFilter: (filter: Filter) => void;
  handleApplyFilter: (filter: Filter | undefined) => void;
  // TODO: Use a better type than `any`.
  getSelectedFilter: (type: any) => any;
  state: FilterState;
  getFilters: () => Filter[];
  getState: () => FilterState;
  setState: React.Dispatch<React.SetStateAction<FilterState>>;
  filterCountsData: FilterTypeCount;
  getModelFiltersCount: (
    makeName: string,
  ) => Promise<Array<FilterCountModelsQuery['filterCountModels'][0] & IsSelectable>>;
  handleRemoveMakeFilter: (makeSelectedValues: string[], makeFilter?: MakeFilter) => void;
  persistState: () => void;
  hideOptions: boolean;
  setHideOptions: React.Dispatch<React.SetStateAction<boolean>>;
  openFiltersMobile: boolean;
  setOpenFiltersMobile: React.Dispatch<React.SetStateAction<boolean>>;
  // TODO: Use a better type than `any`.
  getSelectedModelFilterByKey: (keyParentValue: string) => any;
  vehicleStatuses: Array<VehicleStatus>;
  setVehicleStatuses: React.Dispatch<React.SetStateAction<Array<VehicleStatus>>>;
  getSelectedFilters: () => any[];
  getSortBy: () => VehicleSortByOption;
  setSortBy: (sortBy: VehicleSortByOption) => void;
  getSortValue: () => string[] | undefined;
  searchQueryVariable: string;
  setSearchQueryVariable: React.Dispatch<React.SetStateAction<string>>;
  clearAllFilters: () => void;
  /**
   * @description Keeps track of the selected filter's title, which is used for mobile filters feature.
   */
  selectedFilterTitle: string;
  setSelectedFilterTitle: React.Dispatch<React.SetStateAction<string>>;
  listingsTags: Array<Tag>;
  setListingsTags: React.Dispatch<React.SetStateAction<Array<Tag>>>;
  getStatusesFiltersCount: () => Promise<FilterCountStatusesQuery['filterCountStatuses'] & IsSelectable>;
  getOrganizationFilterCount: () => Promise<
    Array<FilterCountOrganizationsQuery['filterCountOrganizations'][0] & IsSelectable>
  >;
  organizationsFilterData: Array<OrganizationFilterData>;
  setOrganizationsFilterData: React.Dispatch<React.SetStateAction<Array<OrganizationFilterData>>>;
  loadFilterCounts: () => void;
};

export const FilterContext = React.createContext<FiltersContext>(undefined);

type FilterProviderInput = {
  children: any;
};

interface FilterState {
  filters: any[];
  sortBy: VehicleSortByOption;
  date: Date;
}

interface IsSelectable {
  isSelected?: boolean;
}

interface FilterCount extends IsSelectable {
  key?: string;
  count?: number;
  __typename: 'FilterElement';
}

interface FilterTypeCount {
  auctionName?: Array<FilterCount>;
  currentRecon?: Array<FilterCount>;
  drivetrain?: Array<FilterCount>;
  engine?: Array<FilterCount>;
  make?: Array<FilterCount>;
  transmission?: Array<FilterCount>;
  trim?: Array<FilterCount>;
  needsParts?: Array<FilterCount>;
  vehicleType?: Array<FilterCount>;
  receivingOrganization?: Array<FilterCount>;
}

export interface VehicleSortByOption {
  key: string;
  title: string;
  ascending: boolean;
}

const FILTER_SCHEMA_VERSION = 4;

const parseFilters = (filterObjects: Array<PossibleFilters>): Filter[] => {
  return filterObjects.map((filterObject) => {
    const filterNamePrefix = filterObject.name.split('_')[0];
    switch (filterNamePrefix) {
      case FilterNamePrefix.YearFilter: {
        return new YearFilter(filterObject);
      }
      case FilterNamePrefix.MakeFilter: {
        return new MakeFilter(filterObject);
      }
      case FilterNamePrefix.ModelFilter: {
        return new ModelFilter(filterObject);
      }
      case FilterNamePrefix.PriceFilter: {
        return new PriceFilter(filterObject);
      }
      case FilterNamePrefix.OdometerFilter: {
        return new OdometerFilter(filterObject);
      }
      case FilterNamePrefix.TransmissionFilter: {
        return new TransmissionFilter(filterObject);
      }
      case FilterNamePrefix.FuelFilter: {
        return new FuelFilter(filterObject);
      }
      case FilterNamePrefix.DrivetrainFilter: {
        return new DrivetrainFilter(filterObject);
      }
      case FilterNamePrefix.EngineFilter: {
        return new EngineFilter(filterObject);
      }
      case FilterNamePrefix.OrganizationsFilter: {
        return new OrganizationsFilter(filterObject);
      }
      case FilterNamePrefix.SharedWithUserFilter: {
        return new SharedWithUserFilter(filterObject);
      }
      case FilterNamePrefix.SavedListingFilter:
        return new SavedListingFilter(filterObject);
      case FilterNamePrefix.ConsumerFilter:
        return new ConsumerFilter(filterObject);
      default: {
        return null;
      }
    }
  });
};

interface WithStickyState {
  schemaVersion: number;
}

const useStickyState = (defaultValue: FilterState, key: string) => {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    const parsedValue: FilterState & WithStickyState = stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    if (parsedValue.schemaVersion < FILTER_SCHEMA_VERSION) {
      return defaultValue;
    }

    parsedValue.filters = parseFilters(parsedValue.filters);
    return parsedValue;
  });

  React.useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify({ schemaVersion: FILTER_SCHEMA_VERSION, ...value }));
  }, [key, value]);

  return [value, setValue] as const;
};

export const sortByOptions: VehicleSortByOption[] = [
  { key: 'createdAt', title: 'Newest to oldest', ascending: false },
  { key: 'createdAt', title: 'Oldest to newest', ascending: true },
];

const VEHICLE_STATUSES_VERSION = 2;
const LISTINGS_TAGS_VERSION = 1;
const FILTERS_VERSION = 5;
const ORG_FILTER_DATA_VERSION = 2;

export const FilterProvider = ({ children }: FilterProviderInput) => {
  const [state, setState] = useStickyState(
    {
      id: '',
      title: '',
      date: new Date(),
      limit: 10,
      filters: [],
      sortBy: sortByOptions[0],
    } as unknown as FilterState,
    `filter-V${FILTERS_VERSION}`,
  );
  const [vehicleStatuses, setVehicleStatuses] = usePersistState<Array<VehicleStatus>>(
    [],
    `vehicle-statuses-v${VEHICLE_STATUSES_VERSION}`,
  );
  const [listingsTags, setListingsTags] = usePersistState([], `listings-tags-v${LISTINGS_TAGS_VERSION}`);

  const [filterCountsData, setFilterCountsData] = React.useState<FilterTypeCount>({});

  // Saves the data fetched for the organizations count filter, so as to map key-value when rendering the filter.
  const [organizationsFilterData, setOrganizationsFilterData] = usePersistState<Array<OrganizationFilterData>>(
    [],
    `organizations-filter-data-v${ORG_FILTER_DATA_VERSION}`,
  );

  const [searchQueryVariable, setSearchQueryVariable] = React.useState('');

  const getSelectedFilters = (): VehicleFilters[] => {
    const filters: VehicleFilters[] = [];
    const filtersLoaded: any = state.filters;
    const filteredFilters = filtersLoaded?.filter(
      (ff) => ff?.attributeKey !== FilterAttributeKey.MAKE && !ff?.parentKeyValue,
    );
    filteredFilters?.forEach((filter: Filter) => filters.push(filter.getQuery()));
    const mmBuilder = new MakeModelFilterBuilder(filtersLoaded);

    if (!mmBuilder.empty()) {
      filters.push(mmBuilder.build());
    }

    if (vehicleStatuses?.length > 0) {
      filters.push({
        stringFilter: {
          field: 'currentStatus.name',
          operator: {
            eq: vehicleStatuses,
          },
        },
      });
    }

    if (listingsTags?.length > 0) {
      filters.push({
        stringFilter: {
          field: 'organizationTagName',
          operator: {
            eq: listingsTags?.map((tag) => tag.name),
          },
        },
      });
    }

    return filters;
  };

  const getSortValue = () => {
    return state.sortBy ? [`${state.sortBy.key}:${state.sortBy.ascending ? 'asc' : 'desc'}`] : undefined;
  };

  /**
   * Updates the given filter with its previously selected options.
   * @param filterAttributeKey
   * @param filterData
   */
  const setPreviouslySelectedFilters = (filterAttributeKey: string, filterData: Array<any>) => {
    const filter = state.filters?.find((f) => f.attributeKey === filterAttributeKey);
    return filterData.map((item) => ({ ...item, isSelected: filter?.values?.includes(item.key) || false }));
  };

  const [loadFilterCountsQuery] = useFilterCountsLazyQuery({
    variables: {
      filters: getSelectedFilters(),
    },
    onCompleted(data) {
      const make = setPreviouslySelectedFilters(FilterAttributeKey.MAKE, data.make);
      const transmission = setPreviouslySelectedFilters(FilterAttributeKey.TRANSMISSION, data.transmission);
      const drivetrain = setPreviouslySelectedFilters(FilterAttributeKey.DRIVETRAIN, data.drivetrain);
      const engine = setPreviouslySelectedFilters(FilterAttributeKey.ENGINE, data.engine);

      setFilterCountsData({
        ...data,
        make,
        transmission,
        drivetrain,
        engine,
      });
    },
    fetchPolicy: 'network-only',
  });

  const [fetchModelFiltersCount] = useFilterCountModelsLazyQuery({
    variables: {
      filters: getSelectedFilters(),
    },
    fetchPolicy: 'network-only',
  });

  const getModelFiltersCount = async (makeName: string): Promise<FilterCountModelsQuery['filterCountModels']> => {
    let dataToReturn = null;
    await fetchModelFiltersCount({
      variables: { makeName },
      onCompleted(data) {
        const modelFilter = state.filters.find((filter) => filter.attributeKey === `ModelFilter_${makeName}`);
        const models = data.filterCountModels?.map((model) => ({
          ...model,
          isSelected: modelFilter?.values?.includes(model.key) || false,
        }));

        dataToReturn = models;
      },
      onError() {
        dataToReturn = [];
      },
    });

    return dataToReturn;
  };

  const [fetchStatusesFiltersCount] = useFilterCountStatusesLazyQuery({
    variables: {
      filters: getSelectedFilters(),
    },
    fetchPolicy: 'network-only',
  });

  const getStatusesFiltersCount = async (): Promise<FilterCountStatusesQuery['filterCountStatuses']> => {
    let dataToReturn = null;
    await fetchStatusesFiltersCount({
      onCompleted(data) {
        const counts = data?.filterCountStatuses?.map((filterCount) => ({
          ...filterCount,
          isSelected: vehicleStatuses?.includes(VehicleStatus[filterCount.key]) || false,
        }));

        dataToReturn = counts;
      },
      onError() {
        dataToReturn = [];
      },
    });

    return dataToReturn;
  };

  const [fetchOrganizationFiltersCount] = useFilterCountOrganizationsLazyQuery({
    variables: {
      filters: getSelectedFilters(),
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'network-only',
  });

  const getOrganizationFilterCount = async (): Promise<FilterCountOrganizationsQuery['filterCountOrganizations']> => {
    let dataToReturn = null;
    await fetchOrganizationFiltersCount({
      onCompleted(data) {
        const organizationFilter = state.filters.find(
          (filter) => filter.attributeKey === FilterAttributeKey.ORGANIZATIONS,
        );
        const organizations = data?.filterCountOrganizations?.map((organization) => ({
          ...organization,
          isSelected: organizationFilter?.values?.includes(organization.key) || false,
        }));

        dataToReturn = organizations;
      },
      onError() {
        dataToReturn = [];
      },
    });

    return dataToReturn;
  };

  const handleRemoveFilter = (filter: Filter) => {
    const filters = state.filters.filter((obj: Filter) => obj !== filter);
    setState({ ...state, filters });
  };

  const removeModelsFilterIfNeeded = (selectedValues: string[]) => {
    const filterAux = state.filters;
    const filters = filterAux.filter((filter) => {
      if (filter?.parentKeyValue) {
        if (!selectedValues.some((value) => value === filter?.parentKeyValue)) {
          return false;
        }
      }
      return true;
    });

    return { ...state, filters };
  };

  /**
   * Removes make filter and its associated models (if there are any).
   */
  const handleRemoveMakeFilter = (makeSelectedValues: string[], makeFilter?: MakeFilter) => {
    const updatedState = removeModelsFilterIfNeeded(makeSelectedValues);
    if (makeFilter && makeFilter.values.length === 0) {
      const filters = updatedState.filters.filter((obj: Filter) => obj !== makeFilter);
      setState({ ...updatedState, filters });
    } else {
      setState({ ...updatedState });
    }
  };

  const persistState = () => {
    setState({ ...state });
  };

  const handleApplyFilter = (filter: Filter | undefined) => {
    /* We need to set the state event if the filter is undefined in order to refresh the view */
    const filters = state.filters;
    if (filter) filters.push(filter);
    setState({ ...state, filters });
  };

  const getFilters = (): Filter[] => {
    return state.filters;
  };

  const getState = (): FilterState => {
    return state;
  };

  const getSortBy = (): VehicleSortByOption => {
    return state.sortBy;
  };

  const setSortBy = (sortBy: VehicleSortByOption) => {
    setState({ ...state, sortBy, date: new Date() });
  };

  const clearAllFilters = () => {
    setState({
      ...state,
      filters: [],
    });
  };

  // TODO: Remove `any` type here.
  const getSelectedFilter = (type: any) => {
    return state.filters.find((filter: Filter) => filter instanceof type);
  };

  // TODO: Ensure that `state.filters` has a better type than `any`.
  const getSelectedModelFilterByKey = (keyParentValue: string) => {
    return state.filters.find(
      (filter: ModelFilter) => filter instanceof ModelFilter && filter.parentKeyValue === keyParentValue,
    );
  };

  /**
   * State to control whether to hide the options within a filter
   */
  const [hideOptions, setHideOptions] = React.useState(false);

  /**
   * State used to control opening the filters on mobile.
   */
  const [openFiltersMobile, setOpenFiltersMobile] = React.useState(false);

  /**
   * State used to keep track of the selected filter on mobile devices.
   */
  const [selectedFilterTitle, setSelectedFilterTitle] = React.useState('');

  return (
    <FilterContext.Provider
      value={{
        handleRemoveFilter,
        handleApplyFilter,
        getSelectedFilter,
        state,
        getFilters,
        getState,
        setState,
        filterCountsData,
        getModelFiltersCount,
        handleRemoveMakeFilter,
        persistState,
        hideOptions,
        setHideOptions,
        openFiltersMobile,
        setOpenFiltersMobile,
        getSelectedModelFilterByKey,
        vehicleStatuses,
        setVehicleStatuses,
        getSelectedFilters,
        getSortBy,
        setSortBy,
        getSortValue,
        searchQueryVariable,
        setSearchQueryVariable,
        clearAllFilters,
        selectedFilterTitle,
        setSelectedFilterTitle,
        listingsTags,
        setListingsTags,
        getStatusesFiltersCount,
        getOrganizationFilterCount,
        organizationsFilterData,
        setOrganizationsFilterData,
        loadFilterCounts: () => void loadFilterCountsQuery(),
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export function useFilterContext() {
  return React.useContext(FilterContext);
}
